import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { useEnterSends } from "@/lib/chatEnter";

const LINE_PX = 20; // leading-5

// Zone de saisie du chat façon WhatsApp : une ligne au départ, grandit jusqu'à `maxLines` puis défile.
// Règle Entrée : PC → Entrée envoie, Maj+Entrée va à la ligne ; téléphone (app native ou écran tactile)
// → Entrée va à la ligne, l'envoi se fait avec le bouton. Les sauts de ligne sont conservés dans le message.
export const ChatTextarea = forwardRef(({ value, onChange, onSend, onKeyDown, maxLines = 5, className = "", style, ...rest }, ref) => {
  const innerRef = useRef(null);
  const enterSends = useEnterSends();
  useImperativeHandle(ref, () => innerRef.current);

  const autosize = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = "0px";
    const cs = getComputedStyle(el);
    const pad = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const max = LINE_PX * maxLines + pad;
    const next = Math.min(el.scrollHeight, max);
    el.style.height = `${Math.max(next, LINE_PX + pad)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [maxLines]);

  useEffect(() => { autosize(); }, [value, autosize]);

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
    if (enterSends && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) { e.preventDefault(); onSend?.(); }
    // Sinon : saut de ligne natif (Maj+Entrée sur PC, Entrée sur téléphone).
  };

  return (
    <textarea
      ref={innerRef}
      rows={1}
      value={value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      enterKeyHint={enterSends ? "send" : "enter"}
      className={`resize-none leading-5 overflow-hidden ${className}`}
      style={{ height: `${LINE_PX}px`, ...style }}
      data-enter-sends={enterSends ? "1" : "0"}
      {...rest}
    />
  );
});
ChatTextarea.displayName = "ChatTextarea";
